import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Draft autosave frequency settings.
 *
 * - `instant`: ~5s throttled autosave (default, legacy behavior).
 * - `30s` / `5min`: extended throttle — gives users a window to undo
 *   in-session before changes are persisted to the draft backend.
 * - `off`: no automatic save. The user must explicitly invoke
 *   "Save draft" (button or Ctrl/Cmd+S) to persist their changes.
 */
export type DraftAutosaveMode = 'instant' | '30s' | '5min' | 'off';

export interface DraftAutosaveOption {
  readonly mode: DraftAutosaveMode;
  /** Whether autosave should fire for this mode. */
  readonly enabled: boolean;
  /** Throttle interval — ignored when `enabled` is false. */
  readonly intervalMs: number;
  /** Full menu label (e.g. "Every 30 seconds"). */
  readonly label: string;
  /** Compact label shown next to "Autosave" in the trigger row. */
  readonly shortLabel: string;
}

/**
 * Single source of truth for autosave modes — drives both the throttle
 * configuration in `useAutosave` and the UI rendered by `DraftStatusMenu`.
 * Order here is the order the radio items appear in the menu.
 */
export const DRAFT_AUTOSAVE_OPTIONS: readonly DraftAutosaveOption[] = [
  { mode: 'instant', enabled: true,  intervalMs:   5_000, label: 'Instant (every 5s)',     shortLabel: 'Instant' },
  { mode: '30s',     enabled: true,  intervalMs:  30_000, label: 'Every 30 seconds',       shortLabel: '30s'     },
  { mode: '5min',    enabled: true,  intervalMs: 300_000, label: 'Every 5 minutes',        shortLabel: '5 min'   },
  { mode: 'off',     enabled: false, intervalMs:   5_000, label: 'Off (manual save only)', shortLabel: 'Off'     },
] as const;

const DRAFT_AUTOSAVE_DEFAULT: DraftAutosaveMode = 'instant';

/** Narrowing type guard — true iff `value` is a known DraftAutosaveMode. */
export const isDraftAutosaveMode = (value: unknown): value is DraftAutosaveMode =>
  typeof value === 'string' && DRAFT_AUTOSAVE_OPTIONS.some((o) => o.mode === value);

/** Resolve a mode to its option, falling back to the default if unknown. */
export const getDraftAutosaveOption = (mode: DraftAutosaveMode): DraftAutosaveOption => {
  const found = DRAFT_AUTOSAVE_OPTIONS.find((o) => o.mode === mode);
  if (found) return found;
  // Safe: DRAFT_AUTOSAVE_DEFAULT is itself a literal mode in the array.
  return DRAFT_AUTOSAVE_OPTIONS.find((o) => o.mode === DRAFT_AUTOSAVE_DEFAULT)!;
};

/**
 * Normalize a raw value (e.g. from persisted state, a hand-edited
 * `localStorage`, or an out-of-band caller) into a valid mode. Used by the
 * `persist` migrator/rehydrator and the public setter so the store never
 * holds a value that would desync the UI radio group from runtime behavior.
 */
const coerceDraftAutosaveMode = (value: unknown): DraftAutosaveMode =>
  isDraftAutosaveMode(value) ? value : DRAFT_AUTOSAVE_DEFAULT;

/**
 * Lightweight, persisted store for UI-level preferences that should survive
 * route changes and reloads. Keep this store small — it's hot-read on every
 * layout mount.
 */
interface UIPreferencesState {
  /** Whether the main side navigation is collapsed (icon-only) or expanded. */
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  /** How often the workflow designer should autosave drafts. */
  draftAutosaveMode: DraftAutosaveMode;
  setDraftAutosaveMode: (mode: DraftAutosaveMode) => void;

  /**
   * Whether the run history list (left sidebar inside the Monitoring view)
   * is expanded. Persisted so that toggling expand/collapse survives tab
   * switches, workflow navigation, and reloads. 
   */
  runListOpen: boolean;
  setRunListOpen: (open: boolean) => void;
  toggleRunList: () => void;
}

export const useUIPreferencesStore = create<UIPreferencesState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      draftAutosaveMode: DRAFT_AUTOSAVE_DEFAULT,
      // Coerce on the setter too so a buggy caller passing an unchecked
      // string can't corrupt the store at runtime.
      setDraftAutosaveMode: (mode) =>
        set({ draftAutosaveMode: coerceDraftAutosaveMode(mode) }),

      runListOpen: true,
      setRunListOpen: (open) => set({ runListOpen: Boolean(open) }),
      toggleRunList: () => set((s) => ({ runListOpen: !s.runListOpen })),
    }),
    {
      name: 'otto-ui-preferences',
      version: 3,
      // Run on every load: clamps `draftAutosaveMode` to a known value so
      // hand-edited localStorage (or a value left over from a future build
      // we downgraded from) can't desync the menu's radio selection from
      // the runtime behavior.
      migrate: (persistedState, version) => {
        const state = (persistedState ?? {}) as Partial<UIPreferencesState>;
        // v1 → v2: draftAutosaveMode didn't exist.
        // v2 → v3: runListOpen didn't exist.
        // Both missing fields fall through to sensible defaults below.
        const draftAutosaveMode =
          version < 2 ? DRAFT_AUTOSAVE_DEFAULT : coerceDraftAutosaveMode(state.draftAutosaveMode);
        const persistedRunListOpen =
          typeof state.runListOpen === 'boolean' ? state.runListOpen : true;
        const runListOpen = version < 3 ? true : persistedRunListOpen;
        return { ...state, draftAutosaveMode, runListOpen };
      },
      // Final safety net for the matching-version path (no migration runs
      // when versions match, so `migrate` above doesn't fire). This catches
      // hand-edits at the current version.
      onRehydrateStorage: () => (rehydrated) => {
        if (!rehydrated) return;
        if (!isDraftAutosaveMode(rehydrated.draftAutosaveMode)) {
          rehydrated.draftAutosaveMode = DRAFT_AUTOSAVE_DEFAULT;
        }
        if (typeof rehydrated.runListOpen !== 'boolean') {
          rehydrated.runListOpen = true;
        }
      },
    },
  ),
);
